import React, { useState, useEffect } from "react";
import { useQuery, useQueries, useQueryClient } from 'react-query'
import { getCumulativeResult, getResultsRSC, getUnits, getPhases } from "data/Querys";
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Loader from 'components/general/Loader'
import { useViewport } from 'components/general/useViewPort'
import PageHeader from "components/common/PageHeader";
import Label from "components/general/Label";
import NotAvailable from "components/common/NotAvailable";
import { useUrlFriendlyToParameters } from "helpers/urlFriendly";
import { getLongPhaseRSC, getLevelRSC, getPhase, getPhaseRSC, getLongDisciplineRSC, getLongEventRSC, getEventRSC } from "helpers/rsc";
import ProgressionRule from "components/common/ProgressionRule";
import SalRace from "components/sports/sal/views/SalRace";
import SalOverallResults from "components/sports/sal/views/SalOverallResults";
import SalRaceIncidents from "components/sports/sal/views/SalRaceIndicidents";
import SalRaceInfo from "components/sports/sal/components/SalRaceInfo";
import { isStartListStatus, isLiveStatus, scheduleIsHigherScheduled } from "helpers/status";
import SalCurrentLeaders from "components/sports/sal/components/SalCurrentLeaders";
import SalFinalSummary from "components/sports/sal/views/SalFinalSummary"
import { SimpleProgressBar } from "components/general/ProgressBar";
import { isKite, isSemiFinal, isFinals, isWindsurfing, isKiteSemifinal, getKiteSemisRsc, isKiteFinal } from "components/sports/sal/helper/SalHelpers"
import SalMedalSeries from "components/sports/sal/views/SalMedalSeries";

// SALM49ER----- Men's Skiff
// SALMFORMKITE- Men's Kite
// SALMILCA7---- Men's Dinghy
// SALMIQFOIL--- Men's Windsurfing
// SALW49ERFX--- Women's Skiff
// SALWFORMKITE- Women's Kite
// SALWILCA6---- Women's Dinghy
// SALWIQFOIL--- Women's Windsurfing
// SALX470------ Mixed Dinghy
// SALXNACRA17-- Mixed Multihull

const SalContext = () => {
  const { mirs_discipline, mirs_rsc, mirs_competition, mirs_language } = useUrlFriendlyToParameters()
  const _phase = getPhase(mirs_rsc)
  const { breakPoint } = useViewport();

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // cumulative result
  const { data: dataCumulative, isError: isErrorCumulative, isLoading: isLoadingCumulative } = useQuery({
    queryKey: ['cumulativeData', mirs_competition, mirs_language, getLongEventRSC(mirs_rsc)],
    queryFn: getCumulativeResult,
    refetchInterval: 5000,
    staleTime: 5000,
    refetchIntervalInBackground: true,
    notifyOnChangeProps: ['data', 'error'],
    onError: (error) =>
      console.error(`Something went wrong: ${error.message}`),
  });


  // event units
  const { data: dataEventUnits, isError: isErrorEventUnits, isLoading: isLoadingEventUnits } = useQuery(
    {
      queryKey: ['unitsEvent', mirs_competition, mirs_language, getEventRSC(mirs_rsc)],
      queryFn: getPhases,
      //refetchInterval: 30000,
      staleTime: Infinity,
      refetchIntervalInBackground: true,
      notifyOnChangeProps: ['data', 'error'],
    }
  );

  // phase units
  const { data: dataUnits, isError: isErrorUnits, isLoading: isLoadingUnits } = useQuery(
    {
      queryKey: ['unitsPhase', mirs_competition, mirs_language, getPhaseRSC(mirs_rsc), getLevelRSC(mirs_rsc) === 'PHASE' ? 'S' : 'Y'],
      queryFn: getUnits,
      //refetchInterval: 30000,
      staleTime: Infinity,
      refetchIntervalInBackground: true,
      notifyOnChangeProps: ['data', 'error'],
    }
  );

  // units results 
  let queryArray = [];
  dataUnits?.phase?.units?.forEach((unit) => {
    queryArray.push({
      queryKey: ['resultsData', mirs_competition, unit.code, mirs_language, getLongPhaseRSC(mirs_rsc)],
      queryFn: getResultsRSC,
      refetchInterval: 5000,
      staleTime: 5000,
      refetchIntervalInBackground: true,
      notifyOnChangeProps: ['data', 'error'],
    })
  });

  // needed to get CurrentExtendedInfos from  dt_current odf at discipline level
  queryArray.push({
    queryKey: ['resultsData', mirs_competition, getLongDisciplineRSC(mirs_rsc), mirs_language, undefined, 'STATUS'],
    queryFn: getResultsRSC,
    refetchInterval: 5000,
    staleTime: 5000,
    refetchIntervalInBackground: true,
    notifyOnChangeProps: ['data', 'error'],
  })

  const results = useQueries(queryArray);
  const isLoading = isLoadingUnits || isLoadingCumulative || isLoadingEventUnits || results.some(result => result.isLoading)
  const isError = isErrorUnits || isErrorCumulative || isErrorEventUnits || results.some(result => result.isError)


  if (isError) { return <NotAvailable />; }
  if (isLoading) { return <Loader />; }

  const realData = results.filter(res => res.data?.results)?.map(res => res.data);
  const data = realData?.find(res => res?.results?.eventUnitCode === mirs_rsc)
  let dataA = undefined
  let dataB = undefined
  if (isKiteSemifinal(mirs_rsc)){
    const semis = getKiteSemisRsc(mirs_rsc)
    if(semis.length > 1){
      dataA = realData?.find(res => res?.results?.eventUnitCode === semis[0])
      dataB = realData?.find(res => res?.results?.eventUnitCode === semis[1])
    }
  }

  let races = [];
  let racePos = 1
  let dataEventUnits_ = dataEventUnits;
  if (isWindsurfing(mirs_rsc) && isFinals(mirs_rsc)) {
    dataEventUnits_ = dataEventUnits?.filter(e => e?.code?.slice(-4) !== 'PREL');
  } else if (isWindsurfing(mirs_rsc) || isKite(mirs_rsc)) {
    dataEventUnits_ = dataEventUnits?.filter(e => e?.code?.slice(-4) === 'PREL');
  }

  dataEventUnits_?.sort((a, b) => b?.order - a?.order)?.forEach(item => {
    item?.units?.filter(i => i?.scheduled === 'Y')?.sort((a, b) => a?.code - b?.code)?.forEach(i => {
      let value = "MR"
      if (i?.code?.slice(-12, -8) === 'PREL') {
        value = i?.code?.slice(-6, -5) === '0' ? i?.code?.slice(-5, -4) : i?.code?.slice(-6, -4);
      }
      if (isWindsurfing(mirs_rsc) && isFinals(mirs_rsc)) {
        if (i?.code?.slice(-12, -8) === 'QFNL') {
          value = "QF";
        } else if (i?.code?.slice(-12, -8) === 'SFNL') {
          value = "SF";
        } else {
          value = 'F';
        }
      }

      races.push({ unit: i?.code, value: value, pos: racePos++ })
    })
  });

  if (isWindsurfing(mirs_rsc) && !isFinals(mirs_rsc) && races?.length > 0) {
    let lastRace = races.pop();
    lastRace.value = 'MARATHON';
    races.push(lastRace);
  }

  const statusData = realData?.find(res => res?.results?.eventUnitCode === getLongDisciplineRSC(mirs_rsc))

  if (!data) { return <NotAvailable /> }

  const isMobile = breakPoint === "sm" || breakPoint === "xs"
  const resultStatus = data?.results?.status?.code;
  const isStartList = isStartListStatus(resultStatus);
  const isLive = isLiveStatus(resultStatus)

  //Config TimeLine
  const intermediates = []
  //let currentRsc
  Array.isArray(dataUnits?.phase?.units) && dataUnits?.phase?.units.forEach((unit, index) => {
    //currentRsc = scheduleIsHigherScheduled(unit?.schedule?.status?.code) ? unit.code : currentRsc
    intermediates.push(
      {
        "code": unit.code,
        "value": unit.shortDescription,
        "pos": index,
        "status": unit?.schedule?.status?.code,
        "active": scheduleIsHigherScheduled(unit?.schedule?.status?.code)
      }
    )
  })

  //const current = intermediates.find(item => item.code === currentRsc)
  const progressBarData = {
    "currentPosition": -1,//  current?.pos,
    "intermediates": intermediates
  }
  const numElements = isMobile ? 4 : breakPoint === "md" ? 8 : intermediates.length;

  const showProgressBar = !(isWindsurfing(mirs_rsc) && isFinals(mirs_rsc));

  let labelTabRace = "HeaderRace";
  if (isWindsurfing(mirs_rsc) && isFinals(mirs_rsc)) {
    if (mirs_rsc?.slice(-12, -8) === 'QFNL') {
      labelTabRace = "LabelQuarterfinal";
    } else if (mirs_rsc.slice(-12, -8) === 'SFNL') {
      labelTabRace = "HeaderSFL";
    } else {
      labelTabRace = 'LabelFinal';
    }
  } else if(isKiteFinal(mirs_rsc)){
    labelTabRace = 'LabelFinal';
  }


  return (
    <>
      <PageHeader data={data} title={data?.results?.eventUnit?.phase?.description} />
      <div className='mirs-page-container'>
      {progressBarData && showProgressBar && <SimpleProgressBar key="progressbar-component" id={`mirs-progressbar-${mirs_rsc}`} data={progressBarData} numElements={numElements} />}

      {!isStartList && !isMobile &&
        <SalRaceInfo data={data} />
      }
      {isLive &&
        <SalCurrentLeaders data={data} statusData={statusData} />
      }
      <ProgressionRule key="progress-component" data={data?.config} filter={{ type: ['QUALIFICATION'], code: ['QUAL_RULE'] }} itemData={'value'} />
      <Tabs id="mirs-navsport" defaultActiveKey={`1-${mirs_rsc}`} className="mb-3 mirs-tab-result" >
        {isKiteSemifinal(mirs_rsc) && dataA &&
          <Tab  eventKey={`1-${mirs_rsc}`} title={<><Label data='HeaderSFL' /> A</>}  >
            <SalRace  data={dataA} />
          </Tab>
        }
        {isKiteSemifinal(mirs_rsc) && dataB && 
          <Tab  eventKey={`1B-${mirs_rsc}`} title={<><Label data='HeaderSFL' /> B</>}  >
            <SalRace  data={dataB} />
          </Tab>
        }
        {!isKiteSemifinal(mirs_rsc) &&
          <Tab eventKey={`1-${mirs_rsc}`} title={(<Label data={labelTabRace} />)}  >
            <SalRace data={data} />
          </Tab>           
        }
        {isKite(mirs_rsc) && isFinals(mirs_rsc)
          ? <Tab eventKey={`2-${mirs_rsc}`} title={isSemiFinal(_phase) ? <Label data='HeaderSemifinalSummary' /> : <Label data='HeaderFinalSummary' />}  >
              <SalFinalSummary data={dataCumulative} dataUnits={dataUnits} />
            </Tab>
          : isWindsurfing(mirs_rsc) && isFinals(mirs_rsc)
            ? <Tab eventKey={`2-${mirs_rsc}`} title={(<Label data='HeaderOverallResults' />)}  >
              <SalMedalSeries data={dataCumulative} dataUnits={dataUnits} races={races} />
            </Tab>
            : <Tab eventKey={`2-${mirs_rsc}`} title={(<Label data='HeaderOverallResults' />)}  >
              <SalOverallResults data={dataCumulative} dataUnits={dataUnits} races={races} />
            </Tab>
        }
        <Tab eventKey={`3-${mirs_rsc}`} title={(<Label data='HeaderRaceIncidents' />)}  >
          <SalRaceIncidents data={data} />
        </Tab>
      </Tabs>
      </div>
    </>
  );
};

export default SalContext;
